import { action, batch } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import {
  limitOrderDtoReceivedEvent,
  type LimitOrder,
} from '@root/entities/limit-order'
import { Network } from '@root/entities/network'
import { type Pair } from '@root/entities/pair'
import { TradeSettings } from '@root/entities/trade-settings'
import { TxSerialized } from '@root/entities/transaction'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import type { Duration, TypeHelper } from '@blade/std-lib'
import type {
  TradeLimitOrderCreatePayload,
  TradeLimitOrderPrepareInstructionsCreatePayload,
  TradeLimitOrdersCreatePayload as TradeLimitOrdersCreatePayloadDto,
  TradeLimitOrderUpdatePayload,
} from '@blade/trade-api-client'
import type { CurrencyAddress } from '@root/entities/currency'
import type { LimitOrderId } from '@root/entities/limit-order/model/limit-order-id'
import type { PairAddress } from '@root/entities/pair'
import type { Provider } from '@root/entities/provider'
import type { WalletAddress, WalletId } from '@root/entities/wallet'
import type BigNumber from 'bignumber.js'

export type CreateLimitOrderBaseRequest = {
  price: Pair.Price
  marketCapUsd: BigNumber
  pairAddress: PairAddress
  currencyAddress: CurrencyAddress
  walletId: WalletId
  walletAddress: WalletAddress
  network: Network.Network
  settings: TradeSettings.TradeSettings
  provider: Provider.Provider
  /**
   * Native for buy, tokens for sell
   */
  amount: BigNumber
  valueLow: BigNumber | undefined
  valueHigh: BigNumber | undefined
  triggerType: LimitOrder.TargetType
  type: LimitOrder.Type
  deadline: Duration.Duration
}

const paramsToDto = (req: CreateLimitOrderBaseRequest) => {
  const dto: TradeLimitOrderCreatePayload = {
    type: req.type,
    walletId: req.walletId,
    walletAddress: req.walletAddress,
    network: Network.toDto(req.network),
    pairAddress: req.pairAddress,
    tokenAddress: req.currencyAddress,
    swapSettings: TradeSettings.toDto(req.settings),
    provider: req.provider.id,
    amount: req.amount.toString(),
    trigger: {
      triggerType: req.triggerType,
      valueLow: req.valueLow?.toString(),
      valueHigh: req.valueHigh?.toString(),
    },
  }

  return dto
}

export const prepareNonceInstructionsRequestAction = action(
  async (ctx, req: CreateLimitOrderBaseRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const dto = paramsToDto(req)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradeLimitOrderPrepareNonceInstructionsCreate,
        [
          {
            ...dto,
          },
        ],
      ),
    )

    return pipe(
      result,
      Result.map((value) => {
        if ('serializedTransaction' in value) {
          return {
            ...value,
            serializedTransaction: TxSerialized(value.serializedTransaction),
          }
        } else {
          return value
        }
      }),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },

  'prepareNonceInstructionsRequestAction',
)

export type CreateLimitOrderInstructionsRequest = CreateLimitOrderBaseRequest &
  (
    | {
        network: typeof Network.Solana
        nonce: NonNullable<
          TradeLimitOrderPrepareInstructionsCreatePayload['nonce']
        >
      }
    | {
        network: Network.EvmNetwork
        permit: NonNullable<
          TradeLimitOrderPrepareInstructionsCreatePayload['permit']
        >
      }
  )

export const prepareCreateLimitOrderInstructionsRequestAction = action(
  async (ctx, req: CreateLimitOrderInstructionsRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const dto = paramsToDto(req)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradeLimitOrderPrepareInstructionsCreate,
        [
          {
            ...dto,
            nonce: 'nonce' in req ? req.nonce : undefined,
            permit: 'permit' in req ? req.permit : undefined,
          },
        ],
      ),
    )

    return pipe(
      result,
      Result.map((value) => {
        if ('serializedTransaction' in value) {
          return {
            ...value,
            serializedTransaction: TxSerialized(value.serializedTransaction),
          }
        } else {
          return {
            ...value,
            signatures: value.signatures.map((s) => TxSerialized(s)),
          }
        }
      }),

      Result.mapError(ApiError.fallbackToUnknown),
    )
  },

  'prepareCreateLimitOrderInstructionsRequestAction',
)

export type UpdateLimitOrderArgs = {
  orderId: LimitOrderId
  network: Network.Network
  walletId: WalletId
  /**
   * Native for buy, tokens for sell
   */
  amount: BigNumber
  valueLow: BigNumber | undefined
  valueHigh: BigNumber | undefined
  triggerType: LimitOrder.TargetType
}

export const prepareEditLimitOrderInstructionsRequestAction = action(
  async (
    ctx,
    {
      orderId,
      amount,
      valueLow,
      valueHigh,
      triggerType,
      network,
    }: UpdateLimitOrderArgs,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const dto: TradeLimitOrderUpdatePayload = {
      network: Network.toDto(network),
      triggerType,
      valueLow: valueLow?.toString(),
      valueHigh: valueHigh?.toString(),
      amount: amount.toString(),
    }

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradeLimitOrderPrepareEditInstructionsCreate,
        [{ orderId }, dto],
      ),
    )

    return pipe(
      result,
      Result.map((value) => {
        return 'serializedTransaction' in value
          ? {
              ...value,
              serializedTransaction: TxSerialized(value.serializedTransaction),
            }
          : {
              ...value,
              signatures: value.signatures.map((signature) =>
                TxSerialized(signature),
              ),
            }
      }),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },

  'prepareEditLimitOrderInstructionsRequestAction',
)

export type CreateLimitOrderRequest = CreateLimitOrderBaseRequest &
  (
    | {
        network: typeof Network.Solana
        nonce: NonNullable<TradeLimitOrderCreatePayload['nonce']>
        signedLimitOrderTxSvm: NonNullable<
          TradeLimitOrderCreatePayload['signedLimitOrderTxSvm']
        >
      }
    | {
        network: Network.EvmNetwork
        permit: NonNullable<TradeLimitOrderCreatePayload['permit']>
        signedLimitOrderTxsEvm: NonNullable<
          TradeLimitOrderCreatePayload['signedLimitOrderTxsEvm']
        >
      }
  )

export const createLimitOrderRequestAction = action(
  async (ctx, req: CreateLimitOrderRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const dto = paramsToDto(req)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeLimitOrderCreate, [
        {
          ...dto,
          nonce: 'nonce' in req ? req.nonce : undefined,
          permit: 'permit' in req ? req.permit : undefined,
          signedLimitOrderTxSvm:
            'signedLimitOrderTxSvm' in req
              ? req.signedLimitOrderTxSvm
              : undefined,
          signedLimitOrderTxsEvm:
            'signedLimitOrderTxsEvm' in req
              ? req.signedLimitOrderTxsEvm
              : undefined,
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      limitOrderDtoReceivedEvent(ctx, result.success)
    }

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'createLimitOrderRequestAction',
)

export const cancelLimitOrderRequestAction = action(
  async (ctx, req: { id: LimitOrderId; network: Network.Network }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeLimitOrderDelete, [
        { orderId: req.id },
        {
          id: req.id,
          network: Network.toDto(req.network),
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      limitOrderDtoReceivedEvent(ctx, result.success)
    }

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )
    return mappedResult
  },
  'cancelLimitOrderRequestAction',
)

export const updateLimitOrderRequestAction = action(
  async (
    ctx,
    {
      orderId,
      req,
      signedInstructions,
    }: {
      orderId: LimitOrderId
      req: UpdateLimitOrderArgs
      signedInstructions:
        | {
            network: Network.EvmNetwork
            signedLimitOrderTxsEvm: NonNullable<
              TradeLimitOrderUpdatePayload['signedLimitOrderTxsEvm']
            >
          }
        | {
            network: typeof Network.Solana
            signedLimitOrderTxSvm: NonNullable<
              TradeLimitOrderUpdatePayload['signedLimitOrderTxSvm']
            >
          }
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const dto: TradeLimitOrderUpdatePayload = {
      amount: req.amount.toString(),
      triggerType: req.triggerType,
      valueLow: req.valueLow?.toString(),
      valueHigh: req.valueHigh?.toString(),
      network: Network.toDto(signedInstructions.network),
    }

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeLimitOrderUpdate, [
        { orderId },
        {
          ...dto,
          signedLimitOrderTxsEvm:
            'signedLimitOrderTxsEvm' in signedInstructions
              ? signedInstructions.signedLimitOrderTxsEvm
              : undefined,
          signedLimitOrderTxSvm:
            'signedLimitOrderTxSvm' in signedInstructions
              ? signedInstructions.signedLimitOrderTxSvm
              : undefined,
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      limitOrderDtoReceivedEvent(ctx, result.success)
    }

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'updateLimitOrderRequestAction',
)

interface TradeLimitOrdersCreatePayload
  extends TypeHelper.OmitKey<TradeLimitOrdersCreatePayloadDto, 'walletIds'> {
  walletIds: Array<WalletId> | undefined
}

export const fetchLimitOrdersRequestAction = action(
  async (ctx, req: TradeLimitOrdersCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeLimitOrdersCreate, [req]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    if (Result.isSuccess(result)) {
      batch(ctx, () => {
        for (const order of result.success.data) {
          limitOrderDtoReceivedEvent(ctx, order)
        }
      })
    }

    return mappedResult
  },
  'fetchLimitOrdersRequestAction',
)
